import { parse, stringify } from 'querystring';
import Template20 from '@carbon/icons-react/es/template/20';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useHistory, useLocation } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import { ActionableFilters } from 'components/molecules/filters/actionable-filter';
import { Filter } from 'components/molecules/filters/types';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { UsersGroupedByCompanyComponents } from 'components/organisms/filter-editors';
import { FilterContainer } from 'components/organisms/projects-report/common';
import { dateFilterOperationOptions } from 'components/organisms/projects-report/common/filters/filter-fields';
import { CustomRangePicker } from 'components/organisms/projects-report/common/range-picker';
import {
  parseDateRange,
  stringifyDateRange,
} from 'components/organisms/projects-report/common/range-picker/transformer';
import {
  DateRange,
  getRangeObject,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { UserEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import { FieldType } from 'components/organisms/tasks/fields';
import { GetSelectEditorComponents } from 'components/organisms/tasks/filter-box/utils';
import {
  DisabledTemplateFilter,
  TemplateFilter,
  TemplateInsightByTemplateId,
  TemplateInsightByTimeTracked,
  TemplateInsightOverview,
  TemplateInsightViews,
} from 'components/organisms/template-insight';
import { RangePickerTrigger } from 'components/pages/projects-report/range-picker-trigger';
import { Argon } from 'components/templates';
import { useHash } from 'hooks/use-hash';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useSearch } from 'hooks/use-search';
import { useTranslation } from 'hooks/use-translation';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { from_search, to_query } from '../filter-transformer';
import {
  removeTemplateFromFilter,
  useTemplateFilters,
} from './use-template-filters';

const RangeOptions = [
  Range.Last30Days,
  Range.Last60Days,
  Range.Last90Days,
  Range.Last180Days,
  Range.LastQuarter,
  Range.Custom,
];

export const TemplateInsight: FC = () => {
  const { push } = useHistory();
  const [hash, _, getNextHash] = useHash({
    from: (value) => {
      const validViewTypes = Object.values(TemplateInsightViews);

      if (!validViewTypes.includes(value.view as TemplateInsightViews)) {
        return { view: TemplateInsightViews.ByTemplate };
      }

      return value;
    },
    to: (value) => value,
  });

  const view = useMemo(() => hash.view as TemplateInsightViews, [hash]);
  const [search, setSearch] = useSearch<{ range: DateRange }>({
    from: (value) => {
      const range = parseDateRange(
        (value.range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      return { range };
    },
    to: (value) => {
      return { range: stringifyDateRange(value.range) };
    },
  });

  const [viewInitialized, setViewInitialized] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>(search.range);

  useDeepCompareEffect(() => {
    if (viewInitialized) {
      setSelectedRange(search.range);
    }
  }, [search, viewInitialized]);

  const getSelectEditorComponents: GetSelectEditorComponents = (field) => {
    if (field.fieldName === 'customer') {
      return UserEditorComponents;
    }
    if (field.fieldName === 'teamMembers') {
      return UsersGroupedByCompanyComponents;
    }

    return undefined;
  };

  const { fields, loading: isFieldsLoading } =
    useProjectFieldsAsFilterableFields();

  const acceptedProjectFields = useMemo(
    () =>
      fields.filter(
        (x) =>
          !excludedProjectFieldsForFilter.includes(x.fieldName) &&
          x.fieldName !== 'partnerCompany',
      ),
    [fields],
  );

  const {
    filter,
    selectFilter,
    clearFilters,
    replaceFilter,
    updateTemplateInFilter,
    templateSelection,
    savedId,
    templateOptions,
    fields_lookup_by_name,
    isTemplateFieldLoading,
    templateField,
  } = useTemplateFilters({ fields: acceptedProjectFields, isFieldsLoading });

  const filterWithoutTemplate = useMemo(
    () => ({
      ...filter,
      criteria: removeTemplateFromFilter(filter.criteria),
    }),
    [filter],
  );

  const [viewSavedQuery, updateViewInLocalStorage] = useLocalStorage(
    'template_insight.saved_view',
    '',
  );
  const { search: queryParams } = useLocation();

  useEffect(() => {
    if (!viewInitialized || isFieldsLoading || isTemplateFieldLoading) {
      return;
    }

    updateViewInLocalStorage(
      `${to_query(filter)}&${stringify({
        range: stringifyDateRange(selectedRange),
      })}`,
    );
  }, [
    filter,
    viewInitialized,
    selectedRange,
    viewSavedQuery,
    updateViewInLocalStorage,
    isFieldsLoading,
    isTemplateFieldLoading,
    view,
  ]);

  const restoreFilterAndRange = useCallback(
    (restore_query: string) => {
      const saved_filter = from_search(fields_lookup_by_name)(restore_query);
      const saved_range = parseDateRange(
        (parse(restore_query).range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      setSearch({ range: saved_range }, true);
      replaceFilter(saved_filter);
      setSelectedRange(saved_range);
    },
    [fields_lookup_by_name, replaceFilter, setSearch],
  );

  useEffect(() => {
    if (viewInitialized || isFieldsLoading || isTemplateFieldLoading) {
      return;
    }

    const restore_query =
      (queryParams ?? '') === '' ? viewSavedQuery : queryParams;

    restoreFilterAndRange(restore_query);
    setViewInitialized(true);
  }, [
    viewInitialized,
    setViewInitialized,
    queryParams,
    restoreFilterAndRange,
    viewSavedQuery,
    isFieldsLoading,
    isTemplateFieldLoading,
    view,
    fields_lookup_by_name,
    setSearch,
    replaceFilter,
  ]);

  const onChangeFilter = useCallback(
    (nextFilter: Filter) => {
      const templateFilter = filter.criteria.find(
        (criteria) => criteria.field.fieldName === 'template',
      );
      const { criteria } = nextFilter;

      if (templateFilter) {
        criteria.push(templateFilter);
      }
      selectFilter({
        ...nextFilter,
        criteria,
      });
    },
    [filter.criteria, selectFilter],
  );

  return (
    <>
      <Helmet>
        <title>{useTranslation('reports.template_insight.title')}</title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link to="/reports">{useTranslation('reports')}</Link>
          </Breadcrumb.Item>
        }
        title={
          <HeaderWithIconTitle
            icon={<Template20 />}
            title={useTranslation('reports.template_insight.title')}
          />
        }
        tabs={
          <CompactMenu
            mode="horizontal"
            selectedKeys={[view as TemplateInsightViews]}
            onClick={({ key }) => {
              push({
                search: viewSavedQuery,
                hash: getNextHash({ view: key as TemplateInsightViews }),
              });
            }}
          >
            <CompactMenu.Item key={TemplateInsightViews.Overview}>
              <CompactMenuTitle
                text={useTranslation('reports.template_insight.tabs.overview')}
              />
            </CompactMenu.Item>
            <CompactMenu.Item key={TemplateInsightViews.ByTemplate}>
              <CompactMenuTitle
                text={useTranslation(
                  'reports.template_insight.tabs.by_template',
                )}
              />
            </CompactMenu.Item>
            <CompactMenu.Item key={TemplateInsightViews.TrackedVsPlanned}>
              <CompactMenuTitle
                text={useTranslation(
                  'reports.template_insight.tabs.tracked_vs_planned',
                )}
              />
            </CompactMenu.Item>
          </CompactMenu>
        }
        actions={{
          primary: (
            <FilterContainer>
              <ActionableFilters
                loading={isFieldsLoading}
                filter={{
                  ...filter,
                  criteria: removeTemplateFromFilter(filter.criteria),
                }}
                fields={acceptedProjectFields}
                onChange={onChangeFilter}
                reset={clearFilters}
                getFilterOperationTypeOptions={{
                  [FieldType.Date]: dateFilterOperationOptions,
                }}
                getSelectEditorComponents={getSelectEditorComponents}
              />
              <CustomRangePicker
                range={selectedRange}
                onDateChange={(range) => setSearch({ range })}
                triggerComponent={<RangePickerTrigger range={selectedRange} />}
                rangeOptions={RangeOptions}
              />
              {view === TemplateInsightViews.ByTemplate ? (
                <TemplateFilter
                  templateField={templateField}
                  isLoading={isTemplateFieldLoading}
                  savedId={savedId}
                  selection={templateSelection}
                  options={templateOptions}
                  updateTemplateInFilter={updateTemplateInFilter}
                />
              ) : (
                <DisabledTemplateFilter />
              )}
            </FilterContainer>
          ),
          secondary: null,
        }}
        content={
          <>
            {viewInitialized ? (
              <>
                {view === TemplateInsightViews.Overview && (
                  <TemplateInsightOverview
                    range={selectedRange}
                    filter={filterWithoutTemplate}
                  />
                )}
                {view === TemplateInsightViews.ByTemplate && (
                  <TemplateInsightByTemplateId
                    range={selectedRange}
                    filter={filter}
                  />
                )}
                {view === TemplateInsightViews.TrackedVsPlanned && (
                  <TemplateInsightByTimeTracked
                    range={selectedRange}
                    filter={filterWithoutTemplate}
                  />
                )}
              </>
            ) : null}
          </>
        }
      />
    </>
  );
};
